import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useEffect} from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {ThemeSwitcherContext} from "@khanacademy/wonder-blocks-theming";
import * as tokens from "@khanacademy/wonder-blocks-tokens";

import ActionBubble from "../../../action-bubble.tsx";
import {LoadingDots} from "../../../loading-dots.tsx";

import caretDownIcon from "@phosphor-icons/core/regular/caret-down.svg";

import "../notifications-animation.less";

import type {ActionBubble as ActionBubbleT} from "@repo/guide-types";

import {useNotificationActionBubbleState} from "./state/use-notification-action-bubble-state.ts";

interface Props {
    /**
     * Indicates if the user prefers reduced motion, in which case animations
     * should be disabled. This prop is passed from the parent component
     * instead of using the hook directly because this component unmounts and
     * remounts with each new notification. The hook always returns true on the
     * first render, so we rely on the parent component to ensure proper
     * animations.
     */
    prefersReducedMotion?: boolean;
    /**
     * Triggered when an action is selected.
     */
    onActionSelected: (action: ActionBubbleT) => void;
    /**
     * An optional style override. Can be used by the parent to customize the
     * styles or positioning.
     */
    style?: StyleType;
}

/**
 * A type of notification that is used to display a set of actions to the user.
 */
export const NotificationActionBubbles = (props: Props): React.ReactNode => {
    const {prefersReducedMotion = true, style, onActionSelected} = props;

    const {
        state: {actions, allActionsDisplayed, displayedActions, isCollapsed},
        handleCollapse,
        handleDidShow,
    } = useNotificationActionBubbleState();

    // If the notification is not visible, is collapsed, or if there
    // are no actions to display, we won't render anything.
    const shouldDisplayActions = !isCollapsed && actions.length > 0;

    useEffect(() => {
        if (shouldDisplayActions) {
            handleDidShow();
        }
        // We only trigger this effect when the component mounts.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- porting from old code.
    }, []);

    // If there are no actions, or if we're nohow actions, don't
    // render anything.
    if (!shouldDisplayActions) {
        return null;
    }

    return (
        <View style={[styles.actionsWrapper, style]}>
            <ThemeSwitcherContext.Provider value="khanmigo">
                {allActionsDisplayed ? (
                    // If we're done displaying the actions, we show the
                    // collapse button to allow users to hide the actions.
                    <IconButton
                        aria-label={t`Hide notifications`}
                        icon={caretDownIcon}
                        kind="tertiary"
                        size="small"
                        style={[
                            styles.action,
                            styles.collapseButton,
                            !prefersReducedMotion && styles.fadeIn,
                        ]}
                        onClick={handleCollapse}
                    />
                ) : (
                    <LoadingDots
                        color={tokens.color.offBlack64}
                        size="xlarge"
                        style={[
                            styles.loadingDots,
                            {
                                marginBottom: `${
                                    (ACTION_BUBBLE_HEIGHT_PX +
                                        ACTIONS_GAP_SIZE_PX) *
                                    displayedActions.length
                                }px`,
                            },
                        ]}
                    />
                )}

                {displayedActions.map((actionBubble) => (
                    <ActionBubble
                        key={actionBubble.label}
                        actions={actionBubble.actions}
                        label={actionBubble.label}
                        style={[
                            styles.action,
                            !prefersReducedMotion && styles.fadeIn,
                        ]}
                        variant={actionBubble.variant}
                        wrapText={false}
                        onActionSelected={() => onActionSelected(actionBubble)}
                    />
                ))}
            </ThemeSwitcherContext.Provider>
        </View>
    );
};

const ACTIONS_GAP_SIZE_PX = tokens.spacing.medium_16;
// This is a bit fragile since the height of the action bubble depends on the
// contents of the action's label and there isn't an easy way to get the
// dynamic height at the time of render. The following value is what the height
// of the action bubble when the content fits on a single line and contains
// only plaintext – this is the guidance given when utilizing these types of
// notifications.
const ACTION_BUBBLE_HEIGHT_PX = 38;

const styles = StyleSheet.create({
    actionsWrapper: {
        position: "relative",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems: "flex-end",
        gap: ACTIONS_GAP_SIZE_PX,
        transition: "all 500ms",
    },
    action: {
        alignSelf: "flex-end",
    },
    fadeIn: {
        animationName: "fadeIn",
        animationDuration: "400ms",
        animationTimingFunction: "ease-out",
    },
    // TODO: Remove these styles when IconButtons support khanmigo theming OOTB.
    collapseButton: {
        color: tokens.color.blue,
        backgroundColor: tokens.color.offWhite,
        borderColor: tokens.color.fadedBlue,
        borderRadius: tokens.border.radius.radius_120,
        borderStyle: "solid",
        borderWidth: tokens.border.width.thin,
        ":active": {
            backgroundColor: tokens.color.fadedBlue8,
            borderRadius: tokens.border.radius.radius_120,
        },
        ":focus": {
            backgroundColor: tokens.color.offWhite,
            borderColor: tokens.color.blue,
            borderWidth: tokens.border.width.thin,
            borderRadius: tokens.border.radius.radius_120,
        },
        ":focus-visible": {
            backgroundColor: tokens.color.offWhite,
            borderColor: tokens.color.blue,
            borderWidth: tokens.border.width.thin,
            borderRadius: tokens.border.radius.radius_120,
            outline: "none",
        },
        ":hover": {
            color: tokens.color.blue,
            borderColor: tokens.color.blue,
            backgroundColor: tokens.color.offWhite,
            borderRadius: tokens.border.radius.radius_120,
        },
    },
    loadingDots: {
        position: "absolute",
        top: "unset", // Make sure there is no conflicting position style
        bottom: "0",
        transition: "margin-bottom 100ms",
    },
});
